import { useEffect, useMemo, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"

import MainLayout from "../../layouts/MainLayout"

import {
  cancelSupplierPayment,
  createSupplierPayment,
  getSupplierPaymentFormData,
  getSupplierPayments,
} from "../../services/supplierPaymentService"


// =========================================================
// HELPERS
// =========================================================

const nowLocal = () => {
  const date = new Date()

  date.setMinutes(
    date.getMinutes() -
      date.getTimezoneOffset()
  )

  return date
    .toISOString()
    .slice(0, 16)
}


const defaultFormData = {
  organization_id: 1,
  supplier_id: "",
  supplier_invoice_id: "",
  payment_date: nowLocal(),
  amount: 0,
  currency: "USD",
  exchange_rate: 1,
  payment_method: "cash",
  reference: "",
  notes: "",
}


const getArray = (
  data,
  ...keys
) => {
  for (const key of keys) {
    if (
      Array.isArray(
        data?.[key]
      )
    ) {
      return data[key]
    }
  }

  return []
}


const formatAmount = (
  value
) => {
  return Number(
    value || 0
  ).toLocaleString(
    "fr-FR",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  )
}


const formatDateTime = (
  value
) => {
  if (!value) return "-"

  const date =
    new Date(value)

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return String(value)
  }

  return date.toLocaleString(
    "fr-FR"
  )
}


const paymentMethodLabels = {
  cash: "Espèces",
  bank_transfer:
    "Virement bancaire",
  mobile_money:
    "Mobile Money",
  cheque: "Chèque",
  other: "Autre",
}


// =========================================================
// METRIC CARD
// =========================================================

function MetricCard({
  label,
  value,
  caption,
  accent = "#2563eb",
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: {
          xs: 1.75,
          sm: 2,
        },

        minWidth: 0,
        minHeight: 106,

        borderRadius: 3.5,

        borderLeft:
          "4px solid",

        borderLeftColor:
          accent,

        boxShadow:
          "0 8px 24px rgba(15, 23, 42, 0.04)",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color:
            "text.secondary",

          fontWeight: 800,

          letterSpacing:
            0.25,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          mt: 0.35,

          fontSize: {
            xs: 23,
            md: 27,
          },

          lineHeight: 1.15,

          fontWeight: 900,

          color: "#0f172a",
        }}
      >
        {value}
      </Typography>

      {caption ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: "block",
            mt: 0.6,
          }}
        >
          {caption}
        </Typography>
      ) : null}
    </Paper>
  )
}


// =========================================================
// PAGE
// =========================================================

function SupplierPaymentsPage() {
  const [
    payments,
    setPayments,
  ] = useState([])

  const [
    suppliers,
    setSuppliers,
  ] = useState([])

  const [
    invoices,
    setInvoices,
  ] = useState([])

  const [
    organizations,
    setOrganizations,
  ] = useState([])

  const [
    formData,
    setFormData,
  ] = useState(
    defaultFormData
  )

  const [
    open,
    setOpen,
  ] = useState(false)

  const [
    loading,
    setLoading,
  ] = useState(false)

  const [
    saving,
    setSaving,
  ] = useState(false)

  const [
    searchTerm,
    setSearchTerm,
  ] = useState("")

  const [
    page,
    setPage,
  ] = useState(0)

  const rowsPerPage = 5

  const [
    snackbar,
    setSnackbar,
  ] = useState({
    open: false,
    severity: "success",
    message: "",
  })


  // =======================================================
  // PAGINATION RESET
  // =======================================================

  useEffect(() => {
    setPage(0)
  }, [searchTerm])


  // =======================================================
  // SNACKBAR
  // =======================================================

  const showSnackbar = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      severity,
      message,
    })
  }


  // =======================================================
  // LOAD
  // =======================================================

  const loadData =
    async () => {
      try {
        setLoading(true)

        const [
          list,
          form,
        ] =
          await Promise.all([
            getSupplierPayments(),

            getSupplierPaymentFormData(),
          ])

        setPayments(
          Array.isArray(list)
            ? list
            : []
        )

        setSuppliers(
          getArray(
            form,
            "suppliers"
          )
        )

        setInvoices(
          getArray(
            form,
            "supplier_invoices",
            "open_invoices",
            "invoices"
          )
        )

        setOrganizations(
          getArray(
            form,
            "organizations"
          )
        )
      } catch (error) {
        console.error(
          error
        )

        showSnackbar(
          "Impossible de charger les paiements fournisseurs.",
          "error"
        )
      } finally {
        setLoading(false)
      }
    }


  useEffect(() => {
    loadData()
  }, [])


  // =======================================================
  // FORM
  // =======================================================

  const handleChange = (
    event
  ) => {
    const {
      name,
      value,
    } = event.target

    const numericFields =
      [
        "organization_id",
        "supplier_id",
        "supplier_invoice_id",
        "amount",
        "exchange_rate",
      ]

    setFormData(
      (previous) => ({
        ...previous,

        [name]:
          numericFields.includes(
            name
          )
            ? value === ""
              ? ""
              : Number(value)
            : value,
      })
    )
  }


  const handleInvoiceChange =
    (
      invoiceId
    ) => {
      const invoice =
        invoices.find(
          (item) =>
            Number(item.id) ===
            Number(invoiceId)
        )

      setFormData(
        (previous) => ({
          ...previous,

          supplier_invoice_id:
            invoiceId
              ? Number(
                  invoiceId
                )
              : "",

          supplier_id:
            invoice
              ? Number(
                  invoice.supplier_id ||
                    0
                )
              : "",

          amount:
            invoice
              ? Number(
                  invoice.balance_due ??
                    invoice.remaining_amount ??
                    invoice.amount_due ??
                    invoice.total_amount ??
                    0
                )
              : 0,

          currency:
            invoice?.currency ||
            previous.currency ||
            "USD",

          exchange_rate:
            Number(
              invoice?.exchange_rate ||
                previous.exchange_rate ||
                1
            ),
        })
      )
    }


  const resetForm =
    () => {
      setFormData({
        ...defaultFormData,

        payment_date:
          nowLocal(),
      })
    }


  const handleOpen =
    () => {
      resetForm()

      setOpen(true)
    }


  const handleClose =
    () => {
      if (saving) return

      setOpen(false)
    }


  // =======================================================
  // CREATE PAYMENT
  // =======================================================

  const handleSave =
    async () => {
      if (
        !formData.supplier_id ||
        !formData.supplier_invoice_id ||
        Number(
          formData.amount
        ) <= 0
      ) {
        showSnackbar(
          "Sélectionnez une facture et saisissez un montant valide.",
          "warning"
        )

        return
      }

      const payload = {
        organization_id:
          Number(
            formData.organization_id ||
              1
          ),

        supplier_id:
          Number(
            formData.supplier_id
          ),

        supplier_invoice_id:
          Number(
            formData.supplier_invoice_id
          ),

        payment_date:
          new Date(
            formData.payment_date
          ).toISOString(),

        amount:
          Number(
            formData.amount
          ),

        currency:
          formData.currency,

        exchange_rate:
          Number(
            formData.exchange_rate ||
              1
          ),

        payment_method:
          formData.payment_method,

        reference:
          formData.reference
            ?.trim() ||
          null,

        notes:
          formData.notes
            ?.trim() ||
          null,
      }

      try {
        setSaving(true)

        await createSupplierPayment(
          payload
        )

        showSnackbar(
          "Paiement fournisseur enregistré."
        )

        setOpen(false)

        resetForm()

        await loadData()
      } catch (error) {
        console.error(
          error
        )

        showSnackbar(
          error?.response
            ?.data?.detail ||
            "Impossible d'enregistrer le paiement.",
          "error"
        )
      } finally {
        setSaving(false)
      }
    }


  // =======================================================
  // CANCEL PAYMENT
  // =======================================================

  const handleCancel =
    async (
      paymentId
    ) => {
      const confirmed =
        window.confirm(
          "Voulez-vous réellement annuler ce paiement fournisseur ?"
        )

      if (!confirmed) {
        return
      }

      try {
        await cancelSupplierPayment(
          paymentId,
          {}
        )

        showSnackbar(
          "Paiement fournisseur annulé."
        )

        await loadData()
      } catch (error) {
        console.error(
          error
        )

        showSnackbar(
          error?.response
            ?.data?.detail ||
            "Impossible d'annuler le paiement.",
          "error"
        )
      }
    }


  // =======================================================
  // FILTER
  // =======================================================

  const filtered =
    useMemo(() => {
      const query =
        searchTerm
          .toLowerCase()
          .trim()

      if (!query) {
        return payments
      }

      return payments.filter(
        (payment) => {
          const searchable =
            [
              payment.reference,
              payment.supplier_name,
              payment.supplier_invoice_number,
              payment.invoice_number,
              payment.payment_method,
              payment.status,
            ]
              .filter(Boolean)
              .join(" ")
              .toLowerCase()

          return searchable.includes(
            query
          )
        }
      )
    }, [
      payments,
      searchTerm,
    ])


  // =======================================================
  // PAGINATION
  // =======================================================

  const paginatedPayments =
    useMemo(() => {
      const start =
        page *
        rowsPerPage

      return filtered.slice(
        start,
        start +
          rowsPerPage
      )
    }, [
      filtered,
      page,
    ])


  // =======================================================
  // KPI
  // =======================================================

  const activePayments =
    useMemo(
      () =>
        payments.filter(
          (payment) =>
            String(
              payment.status ||
                "confirmed"
            ).toLowerCase() !==
            "cancelled"
        ),
      [payments]
    )


  const cancelledPayments =
    useMemo(
      () =>
        payments.filter(
          (payment) =>
            String(
              payment.status ||
                ""
            ).toLowerCase() ===
            "cancelled"
        ),
      [payments]
    )


  const totalPaid =
    useMemo(
      () =>
        activePayments.reduce(
          (
            total,
            payment
          ) =>
            total +
            Number(
              payment.amount ||
                0
            ),
          0
        ),
      [activePayments]
    )


  // =======================================================
  // RENDER
  // =======================================================

  return (
    <MainLayout>
      <Box
        sx={{
          minWidth: 0,

          pb: {
            xs: 2,
            md: 4,
          },

          "& .MuiPaper-root":
            {
              borderColor:
                "rgba(148, 163, 184, 0.24)",

              backgroundImage:
                "none",
            },

          "& .MuiButton-root":
            {
              textTransform:
                "none",

              fontWeight: 800,

              borderRadius:
                2.5,
            },

          "& .MuiTextField-root .MuiOutlinedInput-root":
            {
              borderRadius:
                2.5,
            },

          "& .MuiAlert-root":
            {
              borderRadius: 3,
            },

          "& .MuiTableHead-root .MuiTableCell-root":
            {
              backgroundColor:
                "#f8fafc",

              color:
                "#334155",

              fontWeight: 800,

              whiteSpace:
                "nowrap",
            },

          "& .MuiTableBody-root .MuiTableRow-root:hover":
            {
              backgroundColor:
                "#f8fbff",
            },

          "& .MuiChip-root":
            {
              fontWeight: 700,
            },
        }}
      >

        {/* =================================================
            HEADER
        ================================================= */}

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns:
              {
                xs: "1fr",

                md:
                  "minmax(0, 1fr) auto",
              },

            alignItems:
              "start",

            columnGap: 2,

            rowGap: 1.25,

            mb: 2.5,

            width: "100%",
          }}
        >
          <Box
            sx={{
              minWidth: 0,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                color:
                  "#0f172a",

                fontWeight: 900,

                fontSize: {
                  xs:
                    "1.65rem",

                  sm:
                    "1.95rem",

                  md:
                    "2.15rem",
                },

                lineHeight: 1.12,

                letterSpacing:
                  "-0.02em",
              }}
            >
              Paiements fournisseurs
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.5,

                maxWidth:
                  760,

                lineHeight:
                  1.55,
              }}
            >
              Enregistrez et suivez les règlements des factures fournisseurs.
            </Typography>
          </Box>


          <Box
            sx={{
              display: "flex",

              justifyContent:
                {
                  xs:
                    "stretch",

                  md:
                    "flex-end",
                },

              justifySelf:
                {
                  xs:
                    "stretch",

                  md: "end",
                },

              width: {
                xs:
                  "100%",

                md:
                  "auto",
              },

              pt: {
                xs: 0.5,

                md: 0,
              },
            }}
          >
            <Button
              variant="contained"
              onClick={
                handleOpen
              }
              sx={{
                width: {
                  xs:
                    "100%",

                  sm:
                    "auto",
                },

                minHeight:
                  44,

                px: 2.5,

                whiteSpace:
                  "nowrap",

                boxShadow:
                  "0 8px 20px rgba(37, 99, 235, 0.18)",
              }}
            >
              + Nouveau paiement
            </Button>
          </Box>
        </Box>


        {/* =================================================
            KPI
        ================================================= */}

        <Box
          sx={{
            display:
              "grid",

            gridTemplateColumns:
              {
                xs:
                  "1fr",

                sm:
                  "repeat(2, minmax(0, 1fr))",

                xl:
                  "repeat(4, minmax(0, 1fr))",
              },

            gap: 1.5,

            mb: 2,
          }}
        >
          <MetricCard
            label="Paiements"
            value={
              payments.length
            }
            caption="Total enregistré"
          />

          <MetricCard
            label="Confirmés"
            value={
              activePayments.length
            }
            caption="Règlements valides"
            accent="#16a34a"
          />

          <MetricCard
            label="Annulés"
            value={
              cancelledPayments.length
            }
            caption="Paiements neutralisés"
            accent="#dc2626"
          />

          <MetricCard
            label="Montant réglé"
            value={
              formatAmount(
                totalPaid
              )
            }
            caption="Toutes devises confondues"
            accent="#0891b2"
          />
        </Box>


        {/* =================================================
            SEARCH
        ================================================= */}

        <Paper
          variant="outlined"
          sx={{
            p: {
              xs: 1.25,
              sm: 1.5,
            },

            mb: 2,

            borderRadius:
              3.5,
          }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="Rechercher un paiement, une référence, une facture ou un fournisseur..."
            value={
              searchTerm
            }
            onChange={(
              event
            ) =>
              setSearchTerm(
                event.target
                  .value
              )
            }
          />
        </Paper>


        {/* =================================================
            TABLE
        ================================================= */}

        <Paper
          variant="outlined"
          sx={{
            overflow:
              "hidden",

            borderRadius:
              3.5,

            boxShadow:
              "0 10px 28px rgba(15, 23, 42, 0.04)",
          }}
        >
          <Box
            sx={{
              px: {
                xs: 1.5,
                sm: 2,
              },

              py: 1.5,

              borderBottom:
                "1px solid",

              borderColor:
                "divider",
            }}
          >
            <Typography
              fontWeight={
                900
              }
              color="#0f172a"
            >
              Historique des paiements
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
            >
              Suivi des règlements enregistrés auprès des fournisseurs.
            </Typography>
          </Box>


          <Box
            sx={{
              overflowX:
                "auto",
            }}
          >
            <Table
              size="small"
              sx={{
                minWidth:
                  1020,
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>
                    Date
                  </TableCell>

                  <TableCell>
                    Fournisseur
                  </TableCell>

                  <TableCell>
                    Facture
                  </TableCell>

                  <TableCell
                    align="right"
                  >
                    Montant
                  </TableCell>

                  <TableCell>
                    Méthode
                  </TableCell>

                  <TableCell>
                    Référence
                  </TableCell>

                  <TableCell>
                    Statut
                  </TableCell>

                  <TableCell
                    align="right"
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>


              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={
                        8
                      }
                      align="center"
                      sx={{
                        py: 5,
                      }}
                    >
                      Chargement...
                    </TableCell>
                  </TableRow>
                ) : filtered.length ===
                  0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={
                        8
                      }
                      align="center"
                      sx={{
                        py: 5,

                        color:
                          "text.secondary",
                      }}
                    >
                      Aucun paiement fournisseur.
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedPayments.map(
                    (
                      payment
                    ) => {
                      const cancelled =
                        String(
                          payment.status ||
                            ""
                        ).toLowerCase() ===
                        "cancelled"

                      return (
                        <TableRow
                          key={
                            payment.id
                          }
                          hover
                        >
                          <TableCell>
                            {formatDateTime(
                              payment.payment_date
                            )}
                          </TableCell>

                          <TableCell>
                            <Typography
                              fontWeight={
                                700
                              }
                            >
                              {payment.supplier_name ||
                                `#${payment.supplier_id}`}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            {payment.supplier_invoice_number ||
                              payment.invoice_number ||
                              `#${payment.supplier_invoice_id}`}
                          </TableCell>

                          <TableCell
                            align="right"
                          >
                            <Typography
                              fontWeight={
                                800
                              }
                              sx={{
                                whiteSpace:
                                  "nowrap",
                              }}
                            >
                              {formatAmount(
                                payment.amount
                              )}{" "}
                              {payment.currency ||
                                "USD"}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            {paymentMethodLabels[
                              payment
                                .payment_method
                            ] ||
                              payment.payment_method ||
                              "-"}
                          </TableCell>

                          <TableCell>
                            {payment.reference ||
                              "-"}
                          </TableCell>

                          <TableCell>
                            <Chip
                              size="small"
                              label={
                                cancelled
                                  ? "Annulé"
                                  : "Confirmé"
                              }
                              color={
                                cancelled
                                  ? "error"
                                  : "success"
                              }
                            />
                          </TableCell>

                          <TableCell
                            align="right"
                          >
                            {!cancelled ? (
                              <Button
                                size="small"
                                color="error"
                                variant="text"
                                onClick={() =>
                                  handleCancel(
                                    payment.id
                                  )
                                }
                              >
                                Annuler
                              </Button>
                            ) : (
                              "-"
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    }
                  )
                )}
              </TableBody>
            </Table>
          </Box>


          <TablePagination
            component="div"
            count={
              filtered.length
            }
            page={page}
            onPageChange={(
              _,
              newPage
            ) =>
              setPage(
                newPage
              )
            }
            rowsPerPage={
              rowsPerPage
            }
            rowsPerPageOptions={[
              5,
            ]}
            labelRowsPerPage="Lignes"
            labelDisplayedRows={({
              from,
              to,
              count,
            }) =>
              `${from}–${to} sur ${count}`
            }
            sx={{
              borderTop:
                "1px solid",

              borderColor:
                "divider",

              "& .MuiTablePagination-toolbar":
                {
                  minHeight:
                    54,

                  px: {
                    xs: 1,
                    sm: 2,
                  },
                },
            }}
          />
        </Paper>
      </Box>


      {/* =================================================
          CREATE DIALOG
      ================================================= */}

      <Dialog
        open={open}
        onClose={
          handleClose
        }
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: {
              xs: 0,
              sm: 4,
            },

            maxHeight: {
              xs:
                "100dvh",

              sm:
                "90dvh",
            },

            m: {
              xs: 0,
              sm: 2,
            },
          },
        }}
      >
        <DialogTitle>
          <Typography
            fontWeight={
              900
            }
            fontSize={
              20
            }
          >
            Nouveau paiement fournisseur
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.35,
            }}
          >
            Sélectionnez une facture ouverte puis renseignez le moyen de règlement.
          </Typography>
        </DialogTitle>


        <DialogContent
          dividers
        >
          <Stack
            spacing={2}
            sx={{
              pt: 0.5,
            }}
          >
            <Box
              sx={{
                display:
                  "grid",

                gridTemplateColumns:
                  {
                    xs:
                      "1fr",

                    md:
                      "repeat(2, minmax(0, 1fr))",
                  },

                gap: 2,
              }}
            >
              <TextField
                select
                size="small"
                label="Organisation"
                name="organization_id"
                value={
                  formData.organization_id
                }
                onChange={
                  handleChange
                }
                fullWidth
              >
                {organizations.length ? (
                  organizations.map(
                    (
                      organization
                    ) => (
                      <MenuItem
                        key={
                          organization.id
                        }
                        value={
                          organization.id
                        }
                      >
                        {organization.name}
                      </MenuItem>
                    )
                  )
                ) : (
                  <MenuItem
                    value={1}
                  >
                    Organisation principale
                  </MenuItem>
                )}
              </TextField>


              <TextField
                select
                size="small"
                label="Facture fournisseur"
                value={
                  formData.supplier_invoice_id
                }
                onChange={(
                  event
                ) =>
                  handleInvoiceChange(
                    event.target
                      .value
                  )
                }
                fullWidth
              >
                {invoices.length ===
                0 ? (
                  <MenuItem
                    disabled
                  >
                    Aucune facture ouverte
                  </MenuItem>
                ) : (
                  invoices.map(
                    (
                      invoice
                    ) => (
                      <MenuItem
                        key={
                          invoice.id
                        }
                        value={
                          invoice.id
                        }
                      >
                        {invoice.supplier_invoice_number ||
                          invoice.invoice_number ||
                          `FACT-${invoice.id}`}
                        {invoice.supplier_name
                          ? ` • ${invoice.supplier_name}`
                          : ""}
                      </MenuItem>
                    )
                  )
                )}
              </TextField>
            </Box>


            <Box
              sx={{
                display:
                  "grid",

                gridTemplateColumns:
                  {
                    xs:
                      "1fr",

                    sm:
                      "repeat(2, minmax(0, 1fr))",

                    md:
                      "repeat(4, minmax(0, 1fr))",
                  },

                gap: 2,
              }}
            >
              <TextField
                type="datetime-local"
                size="small"
                label="Date paiement"
                name="payment_date"
                value={
                  formData.payment_date
                }
                onChange={
                  handleChange
                }
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
              />

              <TextField
                type="number"
                size="small"
                label="Montant"
                name="amount"
                value={
                  formData.amount
                }
                onChange={
                  handleChange
                }
                inputProps={{
                  min: 0,
                  step:
                    "0.01",
                }}
                fullWidth
              />

              <TextField
                select
                size="small"
                label="Devise"
                name="currency"
                value={
                  formData.currency
                }
                onChange={
                  handleChange
                }
                fullWidth
              >
                <MenuItem
                  value="USD"
                >
                  USD
                </MenuItem>

                <MenuItem
                  value="CDF"
                >
                  CDF
                </MenuItem>
              </TextField>

              <TextField
                type="number"
                size="small"
                label="Taux"
                name="exchange_rate"
                value={
                  formData.exchange_rate
                }
                onChange={
                  handleChange
                }
                inputProps={{
                  min: 0,
                  step:
                    "0.0001",
                }}
                fullWidth
              />
            </Box>


            <Box
              sx={{
                display:
                  "grid",

                gridTemplateColumns:
                  {
                    xs:
                      "1fr",

                    md:
                      "repeat(2, minmax(0, 1fr))",
                  },

                gap: 2,
              }}
            >
              <TextField
                select
                size="small"
                label="Mode de paiement"
                name="payment_method"
                value={
                  formData.payment_method
                }
                onChange={
                  handleChange
                }
                fullWidth
              >
                <MenuItem
                  value="cash"
                >
                  Espèces
                </MenuItem>

                <MenuItem
                  value="bank_transfer"
                >
                  Virement bancaire
                </MenuItem>

                <MenuItem
                  value="mobile_money"
                >
                  Mobile Money
                </MenuItem>

                <MenuItem
                  value="cheque"
                >
                  Chèque
                </MenuItem>

                <MenuItem
                  value="other"
                >
                  Autre
                </MenuItem>
              </TextField>

              <TextField
                size="small"
                label="Référence"
                name="reference"
                value={
                  formData.reference
                }
                onChange={
                  handleChange
                }
                fullWidth
              />
            </Box>


            <TextField
              multiline
              minRows={3}
              label="Notes"
              name="notes"
              value={
                formData.notes
              }
              onChange={
                handleChange
              }
              fullWidth
            />
          </Stack>
        </DialogContent>


        <DialogActions
          sx={{
            px: 3,
            py: 2,

            borderTop:
              "1px solid",

            borderColor:
              "divider",
          }}
        >
          <Button
            onClick={
              handleClose
            }
            disabled={
              saving
            }
          >
            Annuler
          </Button>

          <Button
            variant="contained"
            disabled={
              saving
            }
            onClick={
              handleSave
            }
          >
            {saving
              ? "Enregistrement..."
              : "Enregistrer le paiement"}
          </Button>
        </DialogActions>
      </Dialog>


      {/* =================================================
          SNACKBAR
      ================================================= */}

      <Snackbar
        open={
          snackbar.open
        }
        autoHideDuration={
          3500
        }
        onClose={() =>
          setSnackbar(
            (
              previous
            ) => ({
              ...previous,
              open: false,
            })
          )
        }
        anchorOrigin={{
          vertical:
            "top",

          horizontal:
            "right",
        }}
      >
        <Alert
          severity={
            snackbar.severity
          }
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  )
}


export default SupplierPaymentsPage